import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, ConfirmModal, Dropdown, Input, Table } from '../../../../../common/ui';
import Axios from '../../../../../common/util/Axios';
import { loadReturnDocument, recordMoneyRefund } from '../../actions';
import DraftReturn from '../DraftReturn';
import styles from '../styles.module.css';
import {
  formatCredit,
  formatMoney,
  formatReturnDate,
  statusClassName,
} from '../format';

const REFUND_COPY = 'Money Refund does not change the fridge or We owe.';

const columns = [
  { id: 'productName', label: 'Product', minWidth: 160 },
  { id: 'qty', label: 'Quantity', minWidth: 100 },
  {
    id: 'netUnitCost',
    label: 'Net Unit Cost',
    minWidth: 130,
    format: (value) => formatMoney(value),
  },
  {
    id: 'returnValue',
    label: 'Return Value',
    minWidth: 130,
    format: (value) => formatMoney(value),
  },
];

const ReturnDocument = ({
  loadReturnDocument,
  recordMoneyRefund,
  auth: { branchId, companyId },
  supplierReturnState: { document },
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const credit = formatCredit(document?.availableCredit);
  const canRefund = (document?.status === 'Open' || document?.status === 'Partially Settled')
    && Number(document?.availableCredit) > 0;
  const canReplace = canRefund;
  const [refundOpen, setRefundOpen] = useState(false);
  const [confirmRefund, setConfirmRefund] = useState(false);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [paymentMethodType, setPaymentMethodType] = useState('');
  const [methods, setMethods] = useState([]);
  const refundingRef = useRef(false);
  const methodTouchedRef = useRef(false);

  const openRefund = async () => {
    if (refundingRef.current) {
      return;
    }
    setAmount('');
    setReference('');
    setConfirmRefund(false);
    methodTouchedRef.current = false;
    setRefundOpen(true);
    try {
      const res = await Axios.get(
        '/api/dashboard/company-payment-methods/get-all-company-payment-methods',
        { params: { companyId } }
      );
      const filtered = (res.data?.data || []).filter(
        (method) => String(method.paymentMethodType || '').toUpperCase() !== 'CREDIT'
      );
      setMethods(filtered);
      const cash = filtered.find(
        (method) => String(method.paymentMethodType || '').toLowerCase() === 'cash'
      );
      if (!methodTouchedRef.current) {
        setPaymentMethodType(String((cash || filtered[0])?.paymentMethodType || '').toUpperCase());
      }
    } catch {
      setMethods([]);
      setPaymentMethodType('');
    }
  };

  useEffect(() => {
    if (id) loadReturnDocument(id);
  }, [id, branchId, loadReturnDocument]);

  if (document && String(document.id) === String(id) && document.status === 'Draft') {
    return <DraftReturn document={document} />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.pageTitle}>{document?.returnNo || 'Supplier return'}</h1>
          <p className={styles.breadcrumb}>Inventory / Suppliers / Return</p>
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.card}>
          {document && (
            <>
              <div className={styles.headerGrid}>
                <div>
                  <p className={styles.fieldLabel}>Return No.</p>
                  <p className={styles.fieldValue}>{document.returnNo}</p>
                </div>
                <div>
                  <p className={styles.fieldLabel}>Date</p>
                  <p className={styles.fieldValue}>{formatReturnDate(document.returnDate)}</p>
                </div>
                <div>
                  <p className={styles.fieldLabel}>Supplier</p>
                  <p className={styles.fieldValue}>{document.supplierName}</p>
                </div>
                <div>
                  <p className={styles.fieldLabel}>Reason</p>
                  <p className={styles.fieldValue}>{document.reason || ''}</p>
                </div>
                <div>
                  <p className={styles.fieldLabel}>Status</p>
                  <p className={styles.fieldValue}>
                    <span className={statusClassName(styles, document.status)}>{document.status}</span>
                  </p>
                </div>
                {credit && (
                  <div>
                    <p className={styles.fieldLabel}>Available Credit</p>
                    <p className={styles.fieldValue}>{credit}</p>
                  </div>
                )}
              </div>
              {Array.isArray(document.settlements) && document.settlements.length > 0 ? (
                <div className={styles.preview}>
                  {document.settlements.map((row) => (
                    row.method === 'REPLACEMENT' ? (
                      <p key={row.id || `${row.counterDocumentId}-${row.createdAt}`} className={styles.previewSentence}>
                        <button
                          type="button"
                          className={styles.backLink}
                          onClick={() => navigate(
                            row.counterDocumentType === 'supplier_invoice'
                              ? `/inventory/invoices/view/${row.counterDocumentId}`
                              : `/inventory/stocks/update-stock/${row.counterDocumentId}`
                          )}
                        >
                          {[
                            'Replacement',
                            formatMoney(row.amount),
                            row.counterDocumentNo,
                            formatReturnDate(row.createdAt),
                          ].filter((part) => part != null && String(part).trim() !== '').join(' ')}
                        </button>
                      </p>
                    ) : row.method === 'APPLY' ? (
                      <p key={row.id || `${row.counterDocumentId}-${row.createdAt}`} className={styles.previewSentence}>
                        <button
                          type="button"
                          className={styles.backLink}
                          onClick={() => navigate(`/inventory/invoices/view/${row.counterDocumentId}`)}
                        >
                          {[
                            'Applied',
                            formatMoney(row.amount),
                            row.counterDocumentNo,
                            formatReturnDate(row.createdAt),
                          ].filter((part) => part != null && String(part).trim() !== '').join(' ')}
                        </button>
                      </p>
                    ) : (
                      <p key={row.id || `${row.reference}-${row.createdAt}`} className={styles.previewSentence}>
                        {`Money Refund ${formatMoney(row.amount)} ${row.reference || ''} ${formatReturnDate(row.createdAt)}`.trim()}
                      </p>
                    )
                  ))}
                </div>
              ) : null}
              {canReplace ? (
                <div className={styles.actionRow}>
                  <p>Post the return, then record a new receive.</p>
                  <Button onClick={openRefund}>Money Refund</Button>
                  <Button
                    variant="secondary"
                    onClick={() => navigate('/inventory/stocks/new-stock', {
                      state: {
                        relatedSupplierReturnId: document.id,
                        returnNo: document.returnNo,
                        supplierId: document.supplierId,
                      },
                    })}
                  >
                    Add Stock
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => navigate('/inventory/invoices/new-invoice', {
                      state: {
                        relatedSupplierReturnId: document.id,
                        returnNo: document.returnNo,
                        supplierId: document.supplierId,
                      },
                    })}
                  >
                    Supplier Invoice
                  </Button>
                </div>
              ) : null}
              <Table
                columns={columns}
                rows={document.lines || []}
                emptyMessage="No supplier returns in this range."
              />
            </>
          )}
          <button type="button" className={styles.backLink} onClick={() => navigate(-1)}>
            Back
          </button>
        </div>
      </div>
      {refundOpen && !confirmRefund && (
        <div className={styles.popupOverlay} role="dialog" aria-label="Money Refund">
          <div className={styles.popup}>
            <Input id="refund-amount" label="Amount" value={amount} onChange={setAmount} />
            <Input id="refund-reference" label="Reference" value={reference} onChange={setReference} />
            <Dropdown
              id="refund-method"
              label="Payment method"
              value={paymentMethodType}
              onChange={(value) => {
                methodTouchedRef.current = true;
                setPaymentMethodType(value);
              }}
              options={methods.map((method) => ({
                value: String(method.paymentMethodType || '').toUpperCase(),
                label: method.paymentMethodName || method.paymentMethodType,
              }))}
            />
            <p className={styles.previewSentence}>{REFUND_COPY}</p>
            <div className={styles.actionRow}>
              <Button variant="secondary" onClick={() => setRefundOpen(false)}>Cancel</Button>
              <Button onClick={() => setConfirmRefund(true)}>Money Refund</Button>
            </div>
          </div>
        </div>
      )}
      {confirmRefund && (
        <ConfirmModal
          open
          title="Money Refund"
          message={REFUND_COPY}
          confirmLabel="Money Refund"
          cancelLabel="Cancel"
          onClose={() => setConfirmRefund(false)}
          onConfirm={() => {
            if (refundingRef.current) {
              return;
            }
            refundingRef.current = true;
            setRefundOpen(false);
            Promise.resolve(recordMoneyRefund(id, { amount, reference, paymentMethodType })).finally(() => {
              refundingRef.current = false;
            });
          }}
        />
      )}
    </div>
  );
};

ReturnDocument.propTypes = {
  loadReturnDocument: PropTypes.func.isRequired,
  recordMoneyRefund: PropTypes.func.isRequired,
  auth: PropTypes.shape({
    branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  supplierReturnState: PropTypes.shape({
    document: PropTypes.object,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.authState,
  supplierReturnState: state.supplierReturnState,
});

export default connect(mapStateToProps, { loadReturnDocument, recordMoneyRefund })(ReturnDocument);
